import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandIcon } from '@/components/BrandLogo';
import { 
  Plus, Package, Truck, ClipboardCheck, FileText, Building2, 
  LogOut, Home, ChevronRight, TrendingUp, TrendingDown,
  Calendar, BarChart3, Navigation
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Circular Gauge Component
const CircularGauge = ({ value, maxValue, label, subLabel, color, trend, trendValue }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  const radius = 70;
  const strokeWidth = 8;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-4">{label}</p>
      <div className="relative">
        <svg height={radius * 2} width={radius * 2} className="-rotate-90">
          {/* Background circle */}
          <circle
            stroke="#f3f4f6"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          {/* Progress circle */}
          <circle
            stroke={color}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference + ' ' + circumference}
            style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-gray-900">{value}</span>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              <span>{trend >= 0 ? '+' : ''}{trendValue}</span>
            </div>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-3">{subLabel}</p>
    </div>
  );
};

// Sidebar Menu Item
const MenuItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
      active 
        ? 'bg-[#36e961] text-[#0a3042] font-semibold' 
        : 'text-gray-300 hover:bg-white/10 hover:text-white'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-sm">{label}</span>
    {badge && (
      <span className="ml-auto bg-[#36e961] text-[#0a3042] text-xs font-bold px-2 py-0.5 rounded-full">
        {badge}
      </span>
    )}
  </button>
);

// Main Component
const ClientDashboardHome = ({ user, orders, onCreateService, onFilterServices, onSelectService, onNavigateToTab, onLogout }) => {
  const { t, i18n } = useTranslation();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [billingSummary, setBillingSummary] = useState(null);

  // Self-contained fetch, same pattern as ClientBillingTab — so the client sees what
  // they're accumulating right here on the page they land on, not only inside Facturas.
  useEffect(() => {
    let cancelled = false;
    const loadBillingSummary = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${BACKEND_URL}/api/client-invoicing/billing/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          setBillingSummary(await res.json());
        }
      } catch (err) {
        console.error('Error loading billing summary:', err);
      }
    };
    loadBillingSummary();
    return () => {
      cancelled = true;
    };
  }, []);

  // Calculate stats
  const list = Array.isArray(orders) ? orders.filter(Boolean) : [];
  const stats = {
    total: list.length,
    in_progress: list.filter(o => 
      ['assigned', 'por_asignar', 'pending', 'confirmed', 'in_transit'].includes(o.status)
    ).length,
    completed: list.filter(o => o.status === 'delivered').length,
    cancelled: list.filter(o => o.status === 'cancelled' || (typeof o.status === 'string' && o.status.includes('Cancelado'))).length,
  };

  const getRecentOrderStatusConfig = (order) => {
    const raw = order.status || 'pending';
    const status = typeof raw === 'string' && raw.includes('Cancelado') ? 'cancelled' : raw;
    const rows = {
      delivered: { bg: 'bg-green-100', text: 'text-green-700', labelKey: 'service_status_delivered' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', labelKey: 'service_status_cancelled' },
      in_transit: { bg: 'bg-blue-100', text: 'text-blue-700', labelKey: 'service_status_in_transit' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', labelKey: 'service_status_in_progress' },
      assigned: { bg: 'bg-amber-100', text: 'text-amber-700', labelKey: 'service_status_assigned' },
      pending: { bg: 'bg-gray-100', text: 'text-gray-700', labelKey: 'service_status_pending' },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-700', labelKey: 'confirmed' },
      reserved: { bg: 'bg-amber-100', text: 'text-amber-700', labelKey: 'transport_tab_status_reserved' },
      inspected: { bg: 'bg-cyan-100', text: 'text-cyan-800', labelKey: 'service_status_inspected' },
      por_asignar: { bg: 'bg-gray-100', text: 'text-gray-700', labelKey: 'admin_unassigned' },
    };
    const row = rows[status] || rows.pending;
    return { bg: row.bg, text: row.text, label: t(row.labelKey) };
  };

  const trendData = useMemo(() => {
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-ES';
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toLocaleDateString(locale, { month: 'short' }));
    }
    return months.map((month, i) => {
      const factor = (i + 1) / 6;
      return {
        name: month,
        services: Math.round(stats.total * factor * (0.8 + Math.random() * 0.4)),
        completed: Math.round(stats.completed * factor * (0.7 + Math.random() * 0.5)),
      };
    });
  }, [stats.total, stats.completed, i18n.language]);

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    if (menuId === 'dashboard') {
      // Stay on dashboard
    } else {
      onNavigateToTab?.(menuId);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0a3042] flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
              <BrandIcon className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">AutoGo</h1>
              <p className="text-gray-400 text-xs">{t('client_home_client_panel')}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <p className="text-gray-500 text-xs uppercase tracking-wider px-4 mb-2">{t('client_home_nav_general')}</p>
          <MenuItem 
            icon={Home} 
            label={t('dashboard')} 
            active={activeMenu === 'dashboard'}
            onClick={() => handleMenuClick('dashboard')}
          />
          <MenuItem 
            icon={Package} 
            label={t('my_services')} 
            active={activeMenu === 'services'}
            onClick={() => handleMenuClick('services')}
            badge={stats.total > 0 ? stats.total : null}
          />
          <MenuItem 
            icon={Plus} 
            label={t('create_service')} 
            active={activeMenu === 'create'}
            onClick={() => {
              setActiveMenu('create');
              onCreateService?.();
            }}
          />

          <p className="text-gray-500 text-xs uppercase tracking-wider px-4 mb-2 mt-6">{t('client_home_nav_tracking')}</p>
          <MenuItem 
            icon={Navigation} 
            label={t('gps_live_title')} 
            active={activeMenu === 'gps'}
            onClick={() => handleMenuClick('gps')}
          />
          <MenuItem 
            icon={Truck} 
            label={t('client_home_service_status_menu')} 
            active={activeMenu === 'deliveries'}
            onClick={() => handleMenuClick('deliveries')}
            badge={stats.in_progress > 0 ? stats.in_progress : null}
          />
          <MenuItem 
            icon={ClipboardCheck} 
            label={t('client_home_inspections_menu')} 
            active={activeMenu === 'inspections'}
            onClick={() => handleMenuClick('inspections')}
          />

          <p className="text-gray-500 text-xs uppercase tracking-wider px-4 mb-2 mt-6">{t('client_home_nav_account')}</p>
          <MenuItem 
            icon={FileText} 
            label={t('client_home_invoices_menu')} 
            active={activeMenu === 'invoices'}
            onClick={() => handleMenuClick('invoices')}
          />
          <MenuItem 
            icon={Building2} 
            label={t('my_company')} 
            active={activeMenu === 'profile'}
            onClick={() => handleMenuClick('profile')}
          />
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center text-white font-bold">
              {user?.company_name?.[0] || user?.name?.[0] || 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.company_name || user?.name}</p>
              <p className="text-gray-400 text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all text-sm"
          >
            <LogOut className="w-4 h-4" />
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('dashboard')}</h2>
              <p className="text-gray-500 text-sm">{t('client_home_welcome_user', { name: user?.company_name || user?.name || '' })}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>{new Date().toLocaleDateString(i18n.language?.startsWith('en') ? 'en-US' : 'es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <button
                onClick={onCreateService}
                className="flex items-center gap-2 px-4 py-2 bg-[#36e961] hover:bg-[#2dd654] text-[#0a3042] font-semibold rounded-lg transition-all shadow-sm"
              >
                <Plus className="w-4 h-4" />
                {t('create_service')}
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-8">
          {/* Accumulated billing total — visible right away, not only inside Facturas */}
          {billingSummary && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8 flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {t('client_home_accumulated_label')}
                </p>
                <p className="text-3xl font-bold text-[#36e961]">
                  €{(billingSummary.unbilled_total ?? 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t('client_home_accumulated_pending_count', { count: billingSummary.pending_orders_count ?? 0 })}
                </p>
              </div>
              <button
                onClick={() => onNavigateToTab?.('invoices')}
                className="flex items-center gap-1 text-sm text-[#36e961] hover:text-[#2dd654] font-medium"
              >
                {t('client_home_view_billing')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Gauges Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            <CircularGauge 
              value={stats.total}
              maxValue={Math.max(stats.total, 10)}
              label={t('total_services')}
              subLabel={t('client_home_gauge_total_sub')}
              color="#0a3042"
              trend={1}
              trendValue="12%"
            />
            <CircularGauge 
              value={stats.in_progress}
              maxValue={Math.max(stats.total, 10)}
              label={t('in_progress')}
              subLabel={t('client_home_gauge_in_progress_sub')}
              color="#36e961"
              trend={1}
              trendValue={stats.in_progress}
            />
            <CircularGauge 
              value={stats.completed}
              maxValue={Math.max(stats.total, 10)}
              label={t('completed')}
              subLabel={t('client_home_gauge_completed_sub')}
              color="#3b82f6"
              trend={1}
              trendValue="8%"
            />
            <CircularGauge 
              value={stats.cancelled}
              maxValue={Math.max(stats.total, 10)}
              label={t('cancelled')}
              subLabel={t('client_home_gauge_cancelled_sub')}
              color="#ef4444"
              trend={-1}
              trendValue="2%"
            />
          </div>

          {/* Trend Chart */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('client_home_service_trend_title')}</h3>
                <p className="text-sm text-gray-500">{t('client_home_service_trend_subtitle')}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#0a3042]" />
                  <span className="text-sm text-gray-600">{t('client_home_chart_legend_services')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#36e961]" />
                  <span className="text-sm text-gray-600">{t('client_home_chart_legend_completed')}</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => [
                    value,
                    name === 'services' ? t('client_home_chart_legend_services') : t('client_home_chart_legend_completed'),
                  ]}
                />
                <Line 
                  type="monotone" 
                  dataKey="services" 
                  stroke="#0a3042" 
                  strokeWidth={3}
                  dot={{ fill: '#0a3042', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#0a3042' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="completed" 
                  stroke="#36e961" 
                  strokeWidth={3}
                  dot={{ fill: '#36e961', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#36e961' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Services */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('recent_services')}</h3>
                <p className="text-sm text-gray-500">{t('client_home_recent_subtitle')}</p>
              </div>
              <button 
                onClick={() => onFilterServices?.('all')}
                className="text-sm text-[#36e961] hover:text-[#2dd654] font-medium flex items-center gap-1"
              >
                {t('client_home_view_all')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {list.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {list.slice(0, 5).map(order => {
                  const config = getRecentOrderStatusConfig(order);

                  return (
                    <div 
                      key={order.id}
                      onClick={() => onSelectService?.(order.id)}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#0a3042] rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-[#36e961]" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{order.car_plate || `#${order.id?.substring(0,8)}`}</p>
                          <p className="text-sm text-gray-500">{order.origin_city} → {order.destination_city}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                          {config.label}
                        </span>
                        <span className="font-semibold text-gray-900">€{Number.isFinite(Number(order.total_price)) ? Number(order.total_price).toFixed(0) : '0'}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4">{t('client_home_no_services_yet')}</p>
                <button
                  onClick={onCreateService}
                  className="flex items-center gap-2 px-4 py-2 bg-[#36e961] text-[#0a3042] font-medium rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                  {t('client_home_create_first_service_btn')}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientDashboardHome;
